import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, NgModule, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { DialogParams, DialogService } from '../../../core/services/dialog.service';
import { EditTrackerService } from '../../../core/services/edit-tracker.service';
import { HybridCommsService } from '../../../core/services/hybrid.comms.service';
import { StoresService } from '../../../core/services/stores/stores.service';
/** Import Data Model Types */
import { FeatureService, PermissionService } from '../../../core/services/permission';
import { RepoEstimatesService } from '../../../data/repositories/repo-estimates.service';
import { RepoSettingsService } from '../../../data/repositories/repo-settings.service';
import { SharedDataService } from '../../../data/services/shared-data.service';
import { SharedComponentsModule } from '../../../shared-components.module';
import { GqlOffsetPaging, GqlSort } from '../../../shared/graphql/common';
import { EstimateViewStoresService } from '../../estimates/estimate-view/shell';
import { EstimateTableConfig, EstimatesApisService } from '../../estimates/estimates-list2/domain';
import { EstimatesService } from '../../estimates/estimates.service';
import { JobsService } from '../../jobs/jobs.service';
import { LayoutService } from '../layout.service';
import { Sidebar2ToggleService } from '../sidebar.2/sidebar2-toggle.service';
import { MenubarEstimatesModule } from './menubar.estimates.component';
import { MenubarJobsModule } from './menubar.jobs.component';
import { TopbarStateService } from './topbar-state.service';
import { TopbarComponent } from './topbar.component';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
    selector: 'topbar-estimates',
    templateUrl: './topbar.estimates.component.html',
    styleUrls: ['./topbar.estimates.component.scss'],
    standalone: false,
})
export class TopbarEstimatesComponent extends TopbarComponent implements AfterViewInit, OnDestroy, OnInit {
    @Input() estimate: any;
    @Input() lead: any;
    @Input() readonly = false;

    @Output() onUseTemplate = new EventEmitter();

    // @ts-ignore TS2564
    previousStatus: string;
    activeGroup = 'estimates';
    leadsEnabled = false;
    topbarStateSubscription: Subscription;
    templateList = [];
    // @ts-ignore TS2564
    selectedTemplateId: string;
    estimateStatusList: string[] = [];
    estimateStatuses: any[] = [];

    // @ts-ignore TS2564
    public isDisabled: boolean;
    // @ts-ignore TS2564
    isMobile: boolean;

    constructor(
        public permissionService: PermissionService,
        public featureService: FeatureService,
        public jobsService: JobsService,
        public sharedData: SharedDataService,
        public settings: RepoSettingsService,
        private readonly estimatesApi: EstimatesApisService,
        public hybridCommsService: HybridCommsService,
        private topbarStateService: TopbarStateService,
        public sidebar2ToggleService: Sidebar2ToggleService,
        private repoEstimates: RepoEstimatesService,
        private readonly estimateViewStoresService: EstimateViewStoresService,
        public storesService: StoresService,
        public layoutService: LayoutService,
        private editTrackerService: EditTrackerService,
        private dialogService: DialogService,
        private estimatesService: EstimatesService,
        private router: Router
    ) {
        super(permissionService, featureService, jobsService, sharedData, settings, sidebar2ToggleService, layoutService, storesService);
        this.atLeftEnd = true;
        this.atRightEnd = false;

        this.topbarStateSubscription = this.topbarStateService.leadAssigned.subscribe((lead) => (this.lead = lead));
        this.leadsEnabled = featureService.isStateAvailable('leads');
    }

    ngOnInit() {
        this.selectedTemplateId = this.estimate.ID_Estimate;
        if (this.estimate.IsTemplate) {
            // @ts-ignore TS2352
            const config = {
                filter: null,
                hideBuiltIn: this.settings.HideBuiltInTemplates,
                hideConnectedTemplates: true,
                type: 'template',
                status: null,
                paging: { skip: 0, take: 100 } as GqlOffsetPaging,
                sort: { columnName: 'number', direction: 'ASC' } as GqlSort,
            } as EstimateTableConfig;

            this.estimatesApi
                .getEstimates(config)
                .pipe(untilDestroyed(this))
                .subscribe((result: any) => {
                    this.templateList = result?.data?.estimates?.items;
                });
        }
        this.repoEstimates.GetStatuses().then((results) => {
            this.estimateStatuses = results;
            this.estimateStatusList = _.uniq(results.map((r) => r.Status));
        });
        this.estimateViewStoresService.isDisabled$.pipe(untilDestroyed(this)).subscribe((isDisabled) => {
            this.isDisabled = isDisabled;
        });
        this.isMobile = this.layoutService.isMobile();
    }

    ngOnDestroy(): void {
        this.topbarStateSubscription.unsubscribe();
    }

    ngAfterViewInit() {
        this.previousStatus = this.estimate.Status;
    }

    setStatus(value: string) {
        if (value === 'TEMPLATE') {
            // @ts-ignore TS2345
            this.templateList.unshift(this.estimate);
        }
        if (this.editTrackerService.isTracking && this.editTrackerService.form) {
            const config: DialogParams = {
                title: 'You have unsaved changes',
                text: 'Please save your changes first, before changing the status.',
                type: 'warning',
                okText: 'OK',
                showCancel: false,
            };
            this.dialogService.show(config).then(() => {
                this._cancelSetStatus();
            });
        } else this._setStatus(value);
    }

    _cancelSetStatus() {
        this.estimate.Status = this.previousStatus;
    }

    // @ts-ignore TS7006
    _setStatus(status) {
        this.estimatesService.setStatus(this.estimate, status).then(
            () => {
                this.previousStatus = status;
            },
            () => {
                this.estimate.Status = this.previousStatus;
            }
        );
    }

    useTemplate() {
        this.onUseTemplate.emit(this.estimate);
    }

    templateChange() {
        this.router.navigate(['/estimates', 'list', this.selectedTemplateId]);
    }
}

@NgModule({
    declarations: [TopbarEstimatesComponent],
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        NgSelectModule,
        MenubarEstimatesModule,
        MenubarJobsModule,
        SharedComponentsModule,
    ],
    exports: [TopbarEstimatesComponent],
})
export class TopbarEstimatesModule {}
